import { Form, SelectField, SubmitButton } from '@/components';
import {
  ReportFormatLabelModifier,
  ReportOrganizingTableFilterSchema,
  type reportFileType,
  reportFormatType,
} from '@/features/mold/models';
import { useGenerateOrganizingTableReport } from '@/features/project/_hooks/report';
import { useOrganizations } from '@/features/tenant-manager/hooks';
import { useForm } from '@/hooks';
import { useMessage } from '@/providers';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof ReportOrganizingTableFilterSchema>;
type Props = {
  fileType: z.infer<typeof reportFileType>;
  fiscalYearOptions: Array<{
    value: string;
    label: string;
  }>;
};

export const ReportOrganizingTableForm: FC<Props> = ({
  fileType,
  fiscalYearOptions,
}) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isValid, isDirty, isSubmitSuccessful },
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      fiscalYear: dayjs().year(),
      formatNumber: 'format1',
    },
    resolver: zodResolver(ReportOrganizingTableFilterSchema),
  });
  const { openMessage } = useMessage();
  const { mutateAsync } = useGenerateOrganizingTableReport();

  const fiscalYear = watch('fiscalYear');

  const { data, isLoading } = useOrganizations({
    params: {
      level: 1,
      showMigrated: true,
      referenceFiscalYear: fiscalYear.toString(),
    },
    select: (data) =>
      data.map((item) => {
        return { label: item.name, value: item.id };
      }),
    enabled: !!fiscalYear,
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await mutateAsync({
        ...values,
        fileType,
      });
      await openMessage(
        'success',
        t('export-file-requested', {
          target: t('report-type-project'),
        }),
      );
    } catch {
      await openMessage(
        'error',
        t('error-message-file-export-failed', {
          fileName: t('report-type-project'),
        }),
      );
    }
  };

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
      style={{ maxWidth: 480 }}
    >
      <SelectField
        name="fiscalYear"
        label={t('fiscal-year')}
        control={control}
        options={fiscalYearOptions}
        errors={errors}
        allowClear={false}
        required
      />
      <SelectField
        name="organizationIds"
        label={t('organization-in-charge')}
        options={data}
        loading={isLoading}
        control={control}
        errors={errors}
        disabled={!fiscalYear}
        mode="multiple"
        required
      />
      <SelectField
        name="formatNumber"
        label={t('report-format')}
        control={control}
        options={reportFormatType.options.map((value) => ({
          value,
          label: ReportFormatLabelModifier[value],
        }))}
        errors={errors}
        allowClear={false}
        required
      />
      <SubmitButton
        text={t('export')}
        loading={isSubmitting}
        disabled={!isValid}
      />
    </Form>
  );
};
